import { Injectable, inject } from '@angular/core';
import * as CustomersActions from '../actions/customers.actions';
import * as fromCustomers from '@root-store/customers-store/index';
import { select, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CustomersService } from '@app/customers/services/customers.service';
import { catchError, concatMap, of, switchMap, withLatestFrom } from 'rxjs';
import { AlertService } from '@core/services/alert.service';
import { CustomerFullDetailResponseDto } from '@core/services/apiclient';

@Injectable()
export class CustomersEffects {
  private readonly store = inject<Store<fromCustomers.State>>(Store);
  private readonly actions = inject(Actions);
  private readonly customersService = inject(CustomersService);
  private readonly alertService = inject(AlertService);

  processArchiveCustomers$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CustomersActions.processArchiveCustomers),
      withLatestFrom(this.store.pipe(select(fromCustomers.selectors.selectCustomerItemsFilters))),
      switchMap(([{ customersId }, filters]) =>
        this.customersService.archiveCustomer(customersId).pipe(
          concatMap(() => [
            CustomersActions.customerOperationSuccess({
              payload: { message: 'customer.info.successfullyArchived', filters: filters },
            }),
          ]),
          catchError((err: unknown) => {
            this.alertService.showApiError(err, 'customer.errors.errorEncounteredWhileArchiving');
            return [
              CustomersActions.customerOperationFailure({
                message: 'customer.errors.errorEncounteredWhileArchiving',
              }),
            ];
          }),
        ),
      ),
    );
  });

  loadCustomerDetails$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromCustomers.actions.loadCustomerDetails),
      switchMap(({ customerId }) =>
        this.customersService.getCustomerDetails(customerId).pipe(
          concatMap((customerDetails: CustomerFullDetailResponseDto) => {
            return [fromCustomers.actions.setCustomerDetails({ customerDetails })];
          }),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  bulkEditCustomers$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromCustomers.actions.buldEditCustomers),
      withLatestFrom(this.store.pipe(select(fromCustomers.selectors.selectCustomerItemsFilters))),
      switchMap(([action, filters]) =>
        this.customersService.bulkEdit(action?.request).pipe(
          concatMap(() => [
            CustomersActions.customerOperationSuccess({
              payload: { message: 'customer.edit.editedSuccessfully', filters: filters },
            }),
          ]),
          catchError((err: unknown) => {
            this.alertService.showApiError(err, 'customer.edit.errorWhenEditing');
            return [
              CustomersActions.customerOperationFailure({
                message: 'customer.edit.errorWhenEditing',
              }),
            ];
          }),
        ),
      ),
    );
  });

  processAnonymizeCustomer$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CustomersActions.processAnonymizeCustomers),
      withLatestFrom(this.store.pipe(select(fromCustomers.selectors.selectCustomerItemsFilters))),
      switchMap(([{ customersId }, filters]) =>
        this.customersService.anonymizeCustomer(customersId).pipe(
          concatMap(() => [
            CustomersActions.customerOperationSuccess({
              payload: { message: 'customer.info.anonymizationSuccessfullyValidated', filters: filters },
            }),
          ]),
          catchError((err: unknown) => {
            this.alertService.showApiError(err, 'customer.errors.errorEncounteredWhileArchiving');
            return [
              CustomersActions.customerOperationFailure({
                message: 'customer.errors.errorEncounteredWhileArchiving',
              }),
            ];
          }),
        ),
      ),
    );
  });

  loadCustomerOperationSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromCustomers.actions.customerOperationSuccess),
      switchMap(({ payload }) => {
        this.alertService.showCustom(payload.message, 'success');
        return [
          CustomersActions.setCustomersListFilters({ filters: [...payload.filters] }),
          CustomersActions.resetCustomerAction(),
        ];
      }),
    );
  });

  // Le toast d'erreur est affiché directement dans les catchError via showApiError
  // (traceId inclus) — plus besoin d'un effect dédié sur customerOperationFailure.

  refreshCustomerInfo$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromCustomers.actions.refreshCustomerInfo),
      withLatestFrom(this.store.pipe(select(fromCustomers.selectors.selectCustomerItemsFilters))),
      concatMap(([{ customerId }, filters]) => {
        return [
          fromCustomers.actions.setCustomersListFilters({ filters: [...filters] }),
          fromCustomers.actions.loadCustomerDetails({ customerId }),
        ];
      }),
    );
  });
}
